import ToggleOffOutlinedIcon from '@mui/icons-material/ToggleOffOutlined';
import ToggleOnOutlinedIcon from '@mui/icons-material/ToggleOnOutlined';
import DecommissionStatus from '~/models/enums/DecommissionStatus.enum';
import ChipsPropertyScripts from '~/models/scripts/ChipsPropertyScripts';
import { LABELS } from "~/resources/Labels";
type SortConfig = {
    key: string;
    direction: string;
};


/**This function is to convert string base64 to Array Buffer */
export const base64ToArrayBuffer = (base64: string) => {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
};

/** This function  allows downloading binary data directly in the browser. */
export const downloadBlob = (arrayBuffer: ArrayBuffer, fileName: string): void => {
    const blob = new Blob([arrayBuffer], { type: 'application/octet-stream' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
};

export function formatDate(dateString: string): string {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
        throw new Error("Invalid date format. Expected format: YYYY-MM-DD");
    }
    
    return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "2-digit"
    });
}
export const sortScripts = (scripts: Array<any>, sortConfig: SortConfig) => {
    // Default to sorting by 'creationDate' descending if no sort key is provided
    if (!sortConfig.key) {
        return [...scripts].sort((a, b) => {
            const aDate = new Date(a.creationDate || 0).getTime();
            const bDate = new Date(b.creationDate || 0).getTime();
            return bDate - aDate; // Descending order
        });
    }

    // Helper function to handle value retrieval with special case for 'cloudProvider'
    const getValue = (script: any, key: string) => {
        if (key === LABELS.CloudProvider) {
            return script.cloudProvider?.name || '';
        }
        if (key === "resourceStatus") {
            return script.resourceStatus || ''; // Handle 'resourceStatus'
        }
        if (key === "requesterUniqueId") {
            return script.requesterUniqueId || ''; // Handle 'requesterUniqueId'
        }
        if (key === "provisionedDateTime" || key === "decomissionedDateTime") {
            return script[key] ? new Date(script[key]).getTime() : 0; // Handle date fields
        }
        return script[key] !== null && script[key] !== undefined ? script[key] : '';
    };

    const sortedScripts = [...scripts].sort((a, b) => {
        const aValue = getValue(a, sortConfig.key);
        const bValue = getValue(b, sortConfig.key);

        // Compare strings
        if (typeof aValue === 'string' && typeof bValue === 'string') {
            const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
            if (comparison !== 0) {
                return sortConfig.direction === LABELS.ASC ? comparison : -comparison;
            }
        } else {
            // Compare numbers or null/undefined
            if (aValue < bValue) return sortConfig.direction === LABELS.ASC ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === LABELS.ASC ? 1 : -1;
        }

        // Secondary sorting by 'creationDate'
        const aDate = new Date(a.creationDate || 0).getTime();
        const bDate = new Date(b.creationDate || 0).getTime();
        return bDate - aDate;
    });

    return sortedScripts;
};



export const sortResources = (resources: Array<any>, sortConfig: SortConfig) => {
    if (!sortConfig?.key) {
        // Default sorting by 'decomissionedDateTime' in descending order
        return [...resources].sort((a, b) => {
            const aDate = new Date(a.decomissionedDateTime || 0).getTime();
            const bDate = new Date(b.decomissionedDateTime || 0).getTime();
            return bDate - aDate; // Newest first
        });
    }

    return [...resources].sort((a, b) => {
        let aValue = a[sortConfig.key] || '';
        let bValue = b[sortConfig.key] || '';

        // Handle string comparison
        if (typeof aValue === 'string' && typeof bValue === 'string') {
            const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
            if (comparison !== 0) {
                return sortConfig.direction === 'ASC' ? comparison : -comparison;
            }
        } else {
            // Handle numeric and date comparisons
            if (aValue < bValue) {
                return sortConfig.direction === 'ASC' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'ASC' ? 1 : -1;
            }
        }

        // Secondary sorting: Ensure 'decomissionedDateTime' prioritizes newer dates
        const aDate = new Date(a.decomissionedDateTime || 0).getTime();
        const bDate = new Date(b.decomissionedDateTime || 0).getTime();
        return bDate - aDate;
    });
};

/** Array Property to fill chips with @param label and @param icon */
 export const chipProperty: ChipsPropertyScripts[] = [{ label: LABELS.Inactive, icon: ToggleOffOutlinedIcon }];

/** 
 * This function handles the logic for changing tabs based on their active or inactive states.
 * It ensures that the active tab is always at index 0 and inactive tab at index 1.
 * 
 * @param activeTab - The currently active tab index.
 * @param activeState - The state of the currently active tab (active or inactive).
 * @param inactiveState - The state of the currently inactive tab (active or inactive).
 * @param setActiveTab - A function to update the active tab index.
 */
export const handleTabChange = (
  activeTab: boolean,
  activeState: string,
  inactiveState: string,
  setActiveTab: (tab: boolean) => void
): void => {
  
  chipProperty.length = 0;

  const isInactive = (state: string, tab: boolean) => state === LABELS.Inactive && tab === true;
  const isActive = (state: string, tab: boolean) => state === LABELS.Active && tab === false;

  if (isInactive(inactiveState, activeTab)) {
    setActiveTab(false);
    chipProperty.push({ label: LABELS.Inactive, icon: ToggleOffOutlinedIcon });
  } else if (isActive(activeState, activeTab)) {
    setActiveTab(true);
    chipProperty.push({ label: LABELS.Active, icon: ToggleOnOutlinedIcon });
  }
};


export function attachResourcesToScripts(object: any) {
  if (!object || !Array.isArray(object.content)) {
    console.warn('Invalid input:', object);
    return [];
  }

  return object.content.map((item: any) => ({
    ...item?.script,
    configFiles:item.configFiles??[],
    provisionedResources: item?.provisionedResources ?? [],
  }));
}
export function attachResourcesToCloudEnviroments(object: any) {
  if (!object || !Array.isArray(object.content)) {
    console.warn('Invalid input:', object);
    return [];
  }

  return object.content.map((item: any) => ({
    ...item
  }));
}
export function attachResourcesToRequesters(object: any) {
 if (!object || !Array.isArray(object.content)) {
    console.warn('Invalid input:', object);
    return [];
  }

  return object.content.map((item:any) => ({
   ...item,
  }));
}





export function displayEntries(list: Array<any>, limit: number, maxRows: number): Array<any> {
    // If limit is greater than the list length, return all items up to the limit
    const validLimit = limit > list.length ? list.length : limit;
    // Ensure the limit does not exceed maxRows
    const finalLimit = Math.min(validLimit, maxRows);
    return list.slice(0, finalLimit);
}

export function getProvisionedResources(status: DecommissionStatus, provisionedResources: ProvisionedResources[]): ProvisionedResources[] {
  if (status === DecommissionStatus.ACTIVATED) {
    return provisionedResources.filter(resource => resource.resourceProvisionedStatus === DecommissionStatus.ACTIVATED);
  }
  return [];
}


export function getResourceProvisionedStatus(
  provisionedResources: any[], 
  statusKey: string = 'resourceProvisionedStatus'
): string {
  if (provisionedResources.length === 0) return "No resources available";
  if (provisionedResources.length === 1) return provisionedResources[0][statusKey];

  // Get all statuses
  const statuses = provisionedResources.map(res => res[statusKey]);

  // Check if 'ACTIVATED' is present
  const activatedStatuses = statuses.filter(status => status === DecommissionStatus.ACTIVATED);

  if (activatedStatuses.length > 0) {
    return DecommissionStatus.ACTIVATED;
  }

  // Remove consecutive duplicate statuses
  const uniqueStatuses = statuses.reduce<string[]>((acc, status, index, arr) => {
    if (index === 0 || status !== arr[index - 1]) {
      acc.push(status);
    }
    return acc;
  }, []);

  // Only return unique statuses if 'ACTIVATED' is not present
  if (uniqueStatuses.length === 1) {
    return uniqueStatuses[0]; 
  }

  return `Multiple resources found: ${uniqueStatuses.join(", ")}`;
}

export function fixInvalidJSON<T = any>(input: string): T | null {
  try {
    // Try parsing as-is
    return JSON.parse(input);
  } catch {
    try {
      // Try to parse again if it was double-encoded (e.g., escaped JSON string)
      const onceParsed = JSON.parse(input);
      if (typeof onceParsed === 'string') {
        return JSON.parse(onceParsed);
      }
    } catch {
      // Continue to regex-based fix attempt
    }

    try {
      // Attempt to fix common issues (naively)
      let fixed = input;

      // Replace single quotes with double quotes
      fixed = fixed.replace(/'/g, '"');

      // Remove trailing commas
      fixed = fixed.replace(/,\s*([}\]])/g, '$1');

      // Quote unquoted keys: {key: "value"} → {"key": "value"}
      fixed = fixed.replace(/([{,]\s*)([a-zA-Z0-9_]+)\s*:/g, '$1"$2":');

      return JSON.parse(fixed);
    } catch (err) {
      console.error('fixInvalidJSON: Failed to fix input JSON', err);
      return null;
    }
  }
}

export type CloudProvider = {
  id: number;
  cloudProviderName: string;
};

export function getCloudProviderName(cloudProvider: CloudProvider): string {
  return cloudProvider.cloudProviderName;
}
